import { reflect } from '@effector/reflect';
import { Button, Checkbox, PasswordInput, Select, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { not } from 'patronum';
import { createBlockUserModal } from 'features/block-user';
import { createDeleteUserModal } from 'features/delete-user';
import { createUnblockUserModal } from 'features/unblock-user';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import { viewerModel } from 'entities/viewer';
import { USER_ROLES, UserRole } from 'shared/api';
import { $t, translated } from 'shared/lib';
import { userPageModel } from '../../model';
import cls from './user-page-form.module.scss';

const BlockUserModal = createBlockUserModal(userPageModel.blockUserModel);

const UnblockUserModal = createUnblockUserModal(userPageModel.unblockUserModel);

const DeleteUserModal = createDeleteUserModal(userPageModel.deleteUserModel);

const NameField = reflect({
	view: TextInput,
	bind: {
		value: userPageModel.form.fields.name.$value,
		onChange: (e) => userPageModel.form.fields.name.change(e.target.value),
		label: translated('user-page.name'),
		placeholder: translated('user-page.name'),
		readOnly: not(viewerModel.$canWriteUsers),
		errorProps: { 'data-test-id': 'edit-user-name-input-error' },
		error: combine(
			[userPageModel.form.fields.name.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('user-page.name'),
					minLength: USER_NAME_MIN_LENGTH,
					maxLength: USER_NAME_MAX_LENGTH,
				}),
		),
	},
});

const PasswordField = reflect({
	view: PasswordInput,
	bind: {
		value: userPageModel.form.fields.password.$value,
		onChange: (e) => userPageModel.form.fields.password.change(e.target.value),
		label: translated('user-page.password'),
		placeholder: translated('user-page.password'),
		readOnly: not(viewerModel.$canWriteUsers),
		errorProps: { 'data-test-id': 'edit-user-password-input-error' },
		error: combine(
			[userPageModel.form.fields.password.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('user-page.password'),
					minLength: USER_PASSWORD_MIN_LENGTH,
				}),
		),
	},
});

const AccountField = reflect({
	view: TextInput,
	bind: {
		value: userPageModel.$user.map((user) => user?.account.name ?? ''),
		label: translated('user-page.account'),
		readOnly: true,
	},
});

const HasMultiLoginField = reflect({
	view: Checkbox,
	bind: {
		checked: userPageModel.form.fields.hasMultiLogin.$value,
		onChange: (e) => userPageModel.form.fields.hasMultiLogin.change(e.target.checked),
		label: translated('user-page.has-multi-login'),
		disabled: not(viewerModel.$canWriteUsers),
	},
});

const RoleField = reflect({
	view: Select,
	bind: {
		'data-test-id': 'user-page-role-select',
		value: userPageModel.form.fields.role.$value.map((value) => value.toString()),
		onChange: (role: string | null) =>
			userPageModel.form.fields.role.change(Number(role ?? USER_ROLES.admin) as UserRole),
		data: combine([$t, userPageModel.$user]).map(([t, user]) =>
			Object.values(USER_ROLES)
				.filter((role) => (user?.role === USER_ROLES.god ? true : role !== USER_ROLES.god))
				.map((role) => ({
					value: `${role}`,
					label: t(`user-roles.${role}`),
				})),
		),
		readOnly: not(viewerModel.$canWriteUsers),
		label: translated('user-page.role'),
		clearable: false,
	},
});

const NoteField = reflect({
	view: TextInput,
	bind: {
		value: userPageModel.form.fields.note.$value,
		onChange: (e) => userPageModel.form.fields.note.change(e.target.value),
		label: translated('user-page.note'),
		placeholder: translated('user-page.note'),
		readOnly: not(viewerModel.$canWriteUsers),
		errorProps: { 'data-test-id': 'edit-user-note-input-error' },
		error: combine(
			[userPageModel.form.fields.note.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('user-page.note'),
					maxLength: USER_NOTE_MAX_LENGTH,
				}),
		),
	},
});

const SaveButton = reflect({
	view: Button,
	bind: {
		type: 'submit',
		children: translated('user-page.save'),
		loading: userPageModel.editUserMutation.$pending,
		className: cls.userPageFormFooterOption,
		disabled: combine([
			userPageModel.form.$pristine,
			viewerModel.$id,
			userPageModel.$user,
			viewerModel.$canWriteUsers,
			userPageModel.$cannotEdit,
		]).map(
			([pristine, id, user, canWrite, cannotEdit]) =>
				pristine || id === user?.id || !canWrite || cannotEdit,
		),
	},
});

export const UserPageForm = () => {
	const [user, submit] = useUnit([userPageModel.$user, userPageModel.form.submit]);

	return (
		user !== null && (
			<>
				<form
					className={cls.userPageForm}
					onSubmit={(event) => {
						event.preventDefault();
						submit();
					}}
				>
					<NameField />
					<PasswordField />
					<AccountField />
					<RoleField />
					<NoteField />
					<HasMultiLoginField />
					<div className={cls.userPageFormFooter}>
						<SaveButton />
						<div className={cls.userPageFormFooterOptions}>
							{user.isLocked ? (
								<UnblockUserModal.Button
									modalData={user}
									className={cls.userPageFormFooterOption}
								/>
							) : (
								<BlockUserModal.Button
									modalData={user}
									className={cls.userPageFormFooterOption}
								/>
							)}
							<DeleteUserModal.Button
								modalData={user}
								className={cls.userPageFormFooterOption}
							/>
						</div>
					</div>
				</form>
				<BlockUserModal />
				<UnblockUserModal />
				<DeleteUserModal />
			</>
		)
	);
};
